import * as React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import {
    Alert,
    Button,
    FlexLayout,
    StackingLayout,
    Divider,
    Paragraph,
    Link,
    Title,
    Loader,
    NotificationTrigger,
    NotificationItem,
} from '@nutanix-ui/prism-reactjs';

import {
    GET_LOGIN_REQUEST,
    GET_PROVIDERS_LIST,
    GET_WELCOME_BANNER,
    LDAP_TYPE,
    LOCAL_TYPE,
    PENDING,
    REFRESH_ERR_CODE,
} from '../../constants';
import { HANDLE_ERROR, HOME_URL } from '../../constant';
import {
    logger,
    getErrorMsg,
    shouldShowErrorMessage,
    removeSessionValue,
    setSessionValue,
    getSessionValue,
    setWelcomeBannerHasTriggered,
    getWelcomeBannerHasTriggered,
    getVersionString,
    getLoginCustomObj,
    forceRedirect,
    authRequestExist,
    isUserLoggedIn,
    getSessionId,
    setToLocal,
    getServiceProviderEnabled,
} from '../../utils';
import { isNcOnPremDeployment } from 'shared-utils/deploymentUtil';
import {
    apiFetchRequest,
    hideWelcomeBannerAction,
    getWelcomeBannerAction,
    getPrismVersionAction,
    loginRequestUpdate,
    getV4APIsAction,
} from '../../actions';
import { LoginLayout } from '../../components/Common';

// TO-DO: CHANGE LATER TO USE ntnx-I18n library
import getI18nService from '../../i18n/I18n';
import WelcomeBanner from '../../components/WelcomeBanner';
import UserGuideLink from '../UserGuideLink';
import IDPLogin from './IDPLogin.jsx';
import iamApis from '../../api/iam-apis';
import './LoginPage.less';
import AlertMessage from 'components/alert-message';

const i18n = getI18nService();

export class Providers extends React.Component {
    static propTypes = {
        pending: PropTypes.bool,
        history: PropTypes.object,
        connectorsList: PropTypes.array,
        customLoginArray: PropTypes.array,
        location: PropTypes.object,
        apiFetchRequest: PropTypes.func,
        noProvidersMessage: PropTypes.string,
        authProviderMessage: PropTypes.string,
        isServiceProviderEnabled: PropTypes.bool,
        systemConfig: PropTypes.object,
        initialLoginRequest: PropTypes.object,
        softwareVersion: PropTypes.string,
        errorMessage: PropTypes.string,
        shouldPresentAsGeneralError: PropTypes.bool,
        hideWelcomeBannerAction: PropTypes.func,
        getWelcomeBannerAction: PropTypes.func,
        getPrismVersionAction: PropTypes.func,
        loginRequestUpdate: PropTypes.func,
        getV4APIsAction: PropTypes.func,
    };
    static defaultProps = {
        message: i18n.t('Providers.message'),
        noProvidersMessage: i18n.t('Providers.noProviderMessage'),
        authProviderMessage: i18n.t('Providers.authProviderMessage'),
    };

    constructor(props) {
        super(props);
        this.state = {
            search: props.location.search,
            showError: shouldShowErrorMessage(props.location.search),
            errorCode: props.location.search,
            errorMessage: '',
        };
    }

    componentDidMount() {
        // check and route to login page or providers page.
        logger('ActionsButtons', 'check and route to login page or providers page.');

        const iamSessionid = getSessionId();
        this.props.getV4APIsAction({
            name: GET_LOGIN_REQUEST,
            options: iamSessionid,
        });
        // Make get system config and connector API calls
        this.props.getWelcomeBannerAction(this.state.search);
        this.props.getPrismVersionAction(iamApis.GET_PRISM_VERSION);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.pending !== this.props.pending && this.state.idpSearch) {
            const { connectorsList, pending, isServiceProviderEnabled } = this.props;
            const { errorCode, idpSearch } = this.state;

            if (!pending) {
                if (
                    isServiceProviderEnabled &&
                    !connectorsList?.length &&
                    idpSearch &&
                    !errorCode
                ) {
                    this.setState({
                        errorCode: 'error404',
                        showError: true,
                    });
                }
            } else {
                this.setState({
                    errorCode: null,
                    showError: false,
                });
            }
        }

        // Show AlertMessage for non-general errors
        if (
            !this.props.shouldPresentAsGeneralError &&
            this.props.errorMessage?.length &&
            !this.state.showError
        ) {
            this.setState({ showError: true, errorMessage: this.props.errorMessage });
        }

        // Hide AlertMessage and show Notification for general errors
        if (
            this.props.shouldPresentAsGeneralError &&
            this.props.errorMessage?.length &&
            (prevProps.errorMessage !== this.props.errorMessage ||
                prevProps.shouldPresentAsGeneralError !== this.props.shouldPresentAsGeneralError)
        ) {
            if (this.state.showError) {
                this.setState({ showError: false, errorMessage: '' });
            }
            NotificationTrigger.add({
                message: this.props.errorMessage,
                autoDismissDelaySecs: 0,
                type: NotificationItem.NotificationIconType.ERROR,
            });
        }
    }

    /**
     * New lifecycle function to prevent render UI before redirect
     * @param {Object} nextProps - Next Props
     * @param {Object} nextState - Next State
     * @returns {boolean} - whether re-render the component
     */
    shouldComponentUpdate(nextProps, nextState) {
        const { connectorsList, pending, isServiceProviderEnabled, errorMessage } = nextProps;
        const { errorCode } = nextState;
        let updateRender = !pending;
        if (!pending && isServiceProviderEnabled && connectorsList?.length > 0 && !errorMessage) {
            const [firstConnector] = connectorsList;
            this.onHandleGoToLogin(firstConnector)();
        } else if (!pending && !isServiceProviderEnabled && connectorsList?.length > 0) {
            const homeUrl = this.getHomeUrl(connectorsList, errorCode);
            if (homeUrl) {
                forceRedirect(homeUrl);
            } else {
                const isNavigationPass = this.navigateToLogin(connectorsList);
                updateRender = !isNavigationPass;
            }
        }
        return updateRender;
    }

    /**
     * Closes welcome banner and launches request for connectors
     * @returns {void}
     */
    handleCloseWelcomeBanner = () => {
        const { hideWelcomeBannerAction: hideWelcomeBanner } = this.props;
        hideWelcomeBanner(GET_WELCOME_BANNER, false);
        // Has welcome banner exist;
        setWelcomeBannerHasTriggered(true);
    };

    render() {
        const {
            connectorsList,
            systemConfig,
            pending,
            customLoginArray,
            softwareVersion,
            isServiceProviderEnabled,
        } = this.props;

        const localConn = this.getConnectorByType(LOCAL_TYPE);
        const customLoginObj = getLoginCustomObj(customLoginArray, isNcOnPremDeployment());

        const errorMsg = getErrorMsg(this.state.errorCode);

        const providersForm = (
            <StackingLayout itemGap="XL" itemDisplay="block" className="providerpage-form">
                <StackingLayout className="loginpage-form-content">
                    {this.state.showError && (
                        <AlertMessage
                            message={this.props.errorMessage || errorMsg}
                            type={Alert.TYPE.ERROR}
                            showCloseIcon={false}
                        />
                    )}
                    <StackingLayout className="loginpage-btns-wrapper" itemGap="none">
                        {this.getChildrenContent(connectorsList, isServiceProviderEnabled)}
                    </StackingLayout>
                    <FlexLayout alignItems="center" justifyContent="center">
                        <Divider />
                        <Paragraph className="no-wrap" type="secondary">
                            {i18n.t('Vocabulary.or')}
                        </Paragraph>
                        <Divider />
                    </FlexLayout>
                </StackingLayout>
                <Link
                    className="iam-center-text"
                    id="local-link"
                    // These lines added for testing purposes only
                    // First line used in our test and other used by other team.
                    data-testid="providersLinkLocalLogin"
                    data-test="providersLinkLocalLogin"
                    onClick={
                        isServiceProviderEnabled
                            ? this.handleConnectorsRequest
                            : this.onHandleGoToLogin(localConn)
                    }
                >
                    {i18n.t('Providers.nutanixProviderLoginMessage')}
                </Link>
                {getWelcomeBannerHasTriggered() ? null : this.getBanner(systemConfig)}
                <UserGuideLink version={getVersionString(softwareVersion)} />
            </StackingLayout>
        );

        return !pending ? (
            <LoginLayout
                form={providersForm}
                customLoginEle={customLoginObj}
                leftTitle={i18n.t('LoginLayout.reimagineText')}
            />
        ) : (
            <Loader tip="Loading..." overlay={true} />
        );
    }

    /**
     * Decided if redirect and get redirect_url for following situations:
     * 1. 412 error
     * 2. If the user is already logged in
     * @param {Array} connectorsList - connectors list from next props
     * @param {String} errorCode - errorCode from state
     * @returns {String} - return redirect_url
     */
    getHomeUrl(connectorsList, errorCode) {
        const authReqExist = authRequestExist(this.props.initialLoginRequest);
        const isUserLoggedInValue = isUserLoggedIn(
            connectorsList?.length,
            this.props.initialLoginRequest
        );

        /**
         * if IAM AuthN request cookie isn't exist(auth_req_exists == false),
         * or if user already logged in is_user_loggedin === true,
         * then user will force redirect back to Prism landing page.
         */
        if (!authReqExist || isUserLoggedInValue) {
            forceRedirect(HOME_URL);
        }

        /**
         * if IAM AuthN backend session has expired, it will provider page from redirect with "ec=ERR-412"
         * if there isn't 412 before, then will send
         * user back to Prism landing page.
         */
        if (errorCode.includes(REFRESH_ERR_CODE)) {
            const localConn = this.getConnectorByType(LOCAL_TYPE, connectorsList);
            setToLocal(localConn);
            // Keep HANDLE_ERROR setter only call once
            const isSameError = this.sameErrorAsPrev(errorCode);
            if (!isSameError) {
                // Redirect UI to host URL when it get Error Code on first time.
                forceRedirect(HOME_URL);
            }
        }
    }

    /**
     * Decided if need to redirect to login page
     * for following situations:
     * 1. Only local connector
     * 2. Only local + one LDAP connector
     * @param {Array} connectorsList - connectors list from next props
     * @returns {Boolean} - return is the navigation pass
     */
    navigateToLogin = connectorsList => {
        const localConn = this.getConnectorByType(LOCAL_TYPE, connectorsList);
        const adConnector = this.getConnectorByType(LDAP_TYPE, connectorsList);
        // const errorMsg = getErrorMsg(errorCode);

        /**
         * if connectorsList has only a local connector it would redirect to the login screen
         * without showing the providers screen
         */
        if (connectorsList.length === 1 && localConn) {
            this.onHandleGoToLogin(localConn)();
            return true;
        }

        /**
         * if connectors list has 2 connectors local and ldap connector should redirect to the login screen
         * without showing the providers screen but with option to change login between local and ldap
         */
        if (connectorsList.length === 2 && localConn && adConnector) {
            this.onHandleGoToLogin(adConnector)();
            return true;
        }

        return false;
    };

    /**
     *  Fetch for connectors
     * @returns {void}
     */
    handleConnectorsRequest = () => {
        this.props.getV4APIsAction({
            name: GET_PROVIDERS_LIST,
            options: {
                $filter: "name eq 'local'",
            },
        });
    };

    /**
     * Redirects to login screen for connector selected
     * and sets connector in localStorage
     * @param {Boolean} val - val boolean
     * @returns {void}
     *
     */
    isIDPSearch = (val = false) => {
        this.setState({
            idpSearch: val,
        });
    };

    /**
     * Redirects to login screen for connector selected
     * and sets connector in localStorage
     * @param {String} connector - connector object
     * @returns {void}
     *
     */
    onHandleGoToLogin =
        (connector = {}) =>
        () => {
            const { extId } = connector;
            setToLocal(connector);
            removeSessionValue(HANDLE_ERROR);
            this.props.loginRequestUpdate({
                loginProviderId: extId,
            });
        };

    handleOnSelectIDP = name => {
        this.isIDPSearch(name);
        this.props.getV4APIsAction({
            name: GET_PROVIDERS_LIST,
            options: {
                $filter: `name eq '${name}'`,
            },
        });
    };

    /**
     * Get Welcome banner component from systemConfig
     * @param {String} systemConfig - system configure object
     * @returns {HTML} - return welcome banner to display
     */
    getBanner(systemConfig) {
        const bannerStatus = get(systemConfig, 'isEnabled') && !getWelcomeBannerHasTriggered();
        const bannerHTMLContent = get(systemConfig, 'content');

        return (
            <WelcomeBanner
                {...{
                    bannerHTMLContent,
                    bannerStatus,
                }}
                onHandleCloseBanner={this.handleCloseWelcomeBanner}
            />
        );
    }

    /**
     *  Get Children for provider display
     * @param {Array} connectorsList - list of connectors
     * @param {Boolean} isServiceProviderEnabled - true/false
     * @returns {HTML} - return content to display
     */
    getChildrenContent(connectorsList, isServiceProviderEnabled = false) {
        if (isServiceProviderEnabled) {
            return (
                <StackingLayout className="loginpage-form-content" itemGap="L">
                    <IDPLogin
                        rowKeyAttribute="id"
                        rowsData={connectorsList}
                        onSelectIDP={this.handleOnSelectIDP}
                    />
                </StackingLayout>
            );
        }

        const { noProvidersMessage } = this.props;
        // No connectors
        if (!connectorsList?.length) {
            return <Title size="h4">{noProvidersMessage}</Title>;
        }
        // list of connector buttons
        return connectorsList.map(connector => {
            return (
                connector.type !== 'local' && (
                    <Button
                        fullWidth={true}
                        data-test={`providersButton${connector.name}`}
                        id={connector.extId}
                        key={connector.extId}
                        onClick={this.onHandleGoToLogin(connector)}
                    >
                        <span className="overflow-ellipsis" style={{ lineHeight: '24px' }}>
                            {i18n.t('Providers.loginWithConnector', {
                                connectorName: connector.name,
                            })}
                        </span>
                    </Button>
                )
            );
        });
    }

    /**
     * Get connector from connectors list
     * @param {String} type - connector type
     * @param {Array} connectorsList - list of connectors
     * @returns {Object} Connector object
     */
    getConnectorByType = (type, connectorsList = this.props.connectorsList) => {
        return connectorsList?.find?.(connector => connector.type === type);
    };

    /**
     * Check if the error has been get before
     * @param {String} error - errorCode from URL parameter
     * @returns {Boolean} is same error like before
     */
    sameErrorAsPrev = error => {
        const existError = getSessionValue(HANDLE_ERROR);
        // If there is error exist on previous call
        if (existError !== error) {
            // Set flag for new type of error
            setSessionValue(HANDLE_ERROR, error);
        }
        // Return false if error is different compare to before
        return existError === error;
    };
}

const mapStateToProps = state => {
    return {
        pending: state.api[PENDING] || state.api[GET_PROVIDERS_LIST]?.[PENDING] || false,
        systemConfig: state.api.GET_WELCOME_BANNER,
        connectorsList: state.api[GET_PROVIDERS_LIST],
        softwareVersion: state.api.PRISM_VERSION,
        customLoginArray: state.api.CUSTOM_LOGIN,
        initialLoginRequest: state.api[GET_LOGIN_REQUEST],
        isServiceProviderEnabled: getServiceProviderEnabled(state.api[GET_LOGIN_REQUEST]),
        errorMessage: state.api.errorMessage,
        shouldPresentAsGeneralError: state.api.shouldPresentAsGeneralError,
    };
};

export default connect(mapStateToProps, {
    apiFetchRequest,
    hideWelcomeBannerAction,
    getWelcomeBannerAction,
    getPrismVersionAction,
    loginRequestUpdate,
    getV4APIsAction,
})(Providers);
